import React from "react";
import DemoCTA from "./DemoCTA";
import { DEMO_GOALS } from "./demoData";
import { formatCurrencyCompact, formatDate } from "../../utils/formatters";

const TRACK_TYPES = {
    MANUAL: "Manual",
    CASH: "Cash Balance",
    INVESTMENTS: "Investments",
    NET_WORTH: "Net Worth",
};

const ICONS = {
    savings: "💰",
    house: "🏠",
    car: "🚗",
    vacation: "✈️",
    education: "🎓",
    retirement: "🌴",
    emergency: "🛡️",
    investment: "📈",
    debt: "💳",
    other: "🎯",
};

function DemoGoalCard({ goal }) {
    const progress = Math.min(goal.progressPercent || 0, 100);
    const isComplete = progress >= 100;

    return (
        <div className="card p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center text-xl">
                        {ICONS[goal.icon] || "🎯"}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">{goal.name}</h3>
                        <p className="text-xs text-gray-500">
                            {TRACK_TYPES[goal.trackType] || goal.trackType}
                        </p>
                    </div>
                </div>
            </div>

            {/* Progress */}
            <div className="mb-3">
                <div className="flex justify-between items-baseline mb-2">
                    <span className="text-lg font-semibold text-gray-900">
                        {formatCurrencyCompact(goal.currentAmount)}
                    </span>
                    <span className="text-sm text-gray-500">
                        of {formatCurrencyCompact(goal.targetAmount)}
                    </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ${
                            isComplete ? "bg-accent-500" : "bg-accent-500"
                        }`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="flex justify-between items-center mt-2">
                    <span className={`text-sm font-medium ${isComplete ? "text-accent-600" : "text-accent-600"}`}>
                        {progress.toFixed(0)}% complete
                    </span>
                    {goal.targetDate && (
                        <span className="text-xs text-gray-500">
                            Target: {formatDate(goal.targetDate)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function DemoGoalsTab() {
    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
                    <p className="text-gray-500 mt-1">Track your financial milestones</p>
                </div>
                <button
                    className="btn-primary flex items-center gap-2 opacity-50 cursor-not-allowed"
                    disabled
                    title="Sign up to create goals"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New Goal
                </button>
            </div>

            {/* Goals Grid */}
            <div className="grid gap-4 md:grid-cols-2">
                {DEMO_GOALS.map((goal) => (
                    <DemoGoalCard key={goal.id} goal={goal} />
                ))}
            </div>

            <DemoCTA variant="inline" className="mt-6" />
        </div>
    );
}
